import { Injectable } from '@angular/core';

import { BetslipConfig } from '@frontend/sports/common/client-config-data-access';
import { PlacementErrorType } from '@frontend/sports/types/betslip';
import { Observable, map, share } from 'rxjs';

import { PlaceButtonBaseState } from '../../../betplacement/place-button.models';
import { PlaceButtonActionStateService } from '../../../betplacement/services/place-button/place-button-action-state.service';
import { PlaceButtonBaseStateService } from '../../../betplacement/services/place-button/place-button-base-state.service';
import { IPlaceButtonStateService } from '../../../betplacement/services/place-button/viewmodel/interfaces/place-button-interfaces';
import { IPlaceButtonState, PlaceButtonMode } from '../../../betplacement/state';
import { getStakeUpdateErrors } from '../../../validation/services/utils/betslip-errors-utils';
import { TeaserBetErrorStateService } from '../teaser-bet-errors-state.service';

@Injectable()
export class TeaserBetPlaceButtonStateService implements IPlaceButtonStateService {
    state$: Observable<IPlaceButtonState>;

    constructor(
        private buttonBaseStateService: PlaceButtonBaseStateService,
        private teaserErrorStateService: TeaserBetErrorStateService,
        private actionStateService: PlaceButtonActionStateService,
        private betslipConfig: BetslipConfig,
    ) {
        this.state$ = this.buttonBaseStateService.baseState$.pipe(
            map((errorState) => this.getTeaserBetState(errorState)),
            share(),
        );
    }

    private getTeaserBetState({ errorState, externalState }: PlaceButtonBaseState): IPlaceButtonState {
        const teasersErrorState = this.teaserErrorStateService.getTeaserErrorState(
            errorState.errors,
            errorState.types,
            errorState.pickState,
            errorState.rewardTokens,
        );

        const oddsChanged = teasersErrorState.oddsChanged;
        const lockedPicksAmount = teasersErrorState.lockedPicksCount;
        const hasLivePicks = teasersErrorState.hasLivePicks;
        const hasComboPreventionError = teasersErrorState.hasComboPrevention;
        const oddsAccepted = teasersErrorState.oddsAccepted;
        const hasPickWithoutPrice = teasersErrorState.hasPickWithoutPrice;
        const isStakeEmpty = teasersErrorState.isStakeEmpty;
        const hasRewardError = teasersErrorState.hasRewardError;
        const hasBetslipError = errorState.errors.betslipErrors.filter((error) => error.type !== PlacementErrorType.TechnicalError).length > 0;

        const isValid =
            !teasersErrorState.placeBlocking &&
            !isStakeEmpty &&
            !hasPickWithoutPrice &&
            !hasBetslipError &&
            !hasComboPreventionError &&
            !hasRewardError &&
            errorState.placeButton.isValid;

        const isProcessing = Boolean(errorState.placeButton.mode & PlaceButtonMode.Processing) || externalState.isQuickDepositProcessing;
        const stakeUpdateErrors = this.betslipConfig.isMinStakeButtonEnabled ? getStakeUpdateErrors(errorState.errors.slipErrors) : [];

        return {
            changedPicks: { unlockedPicks: 0, lockedPicks: lockedPicksAmount, closedPicks: 0, suspendedPicks: 0 },
            oddsChanged,
            isValid,
            hasLivePicks,
            hasComboPreventionError,
            oddsAccepted,
            action: this.actionStateService.getAction({
                isAuthenticated: errorState.placeButton.isAuthenticated,
                mode: errorState.placeButton.mode,
                hasLockedPick: lockedPicksAmount > 0,
                hasLivePicks,
                oddsChanged,
                oddsAccepted,
                hasComboPreventionError,
                betslipErrors: errorState.errors.betslipErrors,
                hasSuspendedPicks: false,
                stakeUpdateErrors,
            }),
            isProcessing,
            stakes: [],
            isFreeBet: false,
            stakeUpdateErrors,
            isLinear: false,
        };
    }
}
